import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ResultOf } from 'gql.tada'

import { always_default_data } from '@repo/common/helpers/query_helpers'
import { graphql_call as graphql_price_call } from '@repo/common/queries/helpers/pricing_query'
import { graphql_call } from '@repo/common/queries/helpers/server_query'

import { disableKeyWhenMissing } from '../helpers/utilities'
import {
  getTokenNames,
  type TokenName,
  type VaultSymbol,
} from '../products/products_names'

import { query_list_vaults } from './vaults_graphql'
import {
  query_get_daily_price,
  query_get_oldest_price,
} from './vaults_price_graphql'

export const keys = createQueryKeyStore({
  vaults: {
    all: null,
    details: ({ id }: { id: RubyID }) => ({
      queryKey: [{ id }],
      contextQueries: {
        // blockchain
        balances: null,
        total_tokens: null,

        // graphql
        original_price: null,
        history: null,
      },
    }),
  },
})

export type VaultFindBy = {
  id?: RubyID
  symbol?: string
}

export type VaultType = TokenName & {
  symbol: VaultSymbol
  id: RubyID
  token_address: CryptoAddress
  vault_contract_address: CryptoAddress
  vault_contract_history: {
    address: CryptoAddress
    version: 'v1' | 'v2' | 'v3'
  }[]
}

export function useListVaults() {
  const results = useQuery<VaultType[]>({
    queryKey: keys.vaults.all.queryKey,
    queryFn: async () => {
      const data = await graphql_call({ query: query_list_vaults })
      if (data == null) return []

      return data.map((f) => ({
        ...getTokenNames(f.symbol as VaultSymbol),
        id: f.id,
        symbol: f.symbol as VaultSymbol,
        token_address: f.token_address!,
        vault_contract_address: f.vault_contract!.address,
        vault_contract_history: f.vault_contract_history,
      }))
    },
  })

  const data = results.data ?? []

  const vault_find_by = ({ id, symbol }: VaultFindBy) =>
    data.find((f) => f.id == id || f.symbol === symbol)

  return {
    ...results,
    data,
    vault_find_by,
  }
}

export function useVaultInfo(args: VaultFindBy) {
  const { vault_find_by, ...others } = useListVaults()

  return { ...others, data: vault_find_by(args) }
}

export function useGetVaultOriginalPrice({ id }: { id: RubyID | undefined }) {
  return useQuery<{ price: number; date: ISO8601DateTime } | null>({
    enabled: Boolean(id),
    queryKey: keys.vaults.details({ id: disableKeyWhenMissing(id) })._ctx
      .original_price.queryKey,
    queryFn: async () => {
      const result = await graphql_price_call({
        query: query_get_oldest_price,
      })
      if (result == null) return null
      return {
        price: Number(result[0].price_per_token),
        date: dayjs.unix(Number(result[0].block_timestamp_s)).toISOString(),
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export type VaultHistoryType = ResultOf<
  typeof query_get_daily_price
>['vaultPriceDayDatas'][number]

export function useGetVaultHistory({ id }: { id: RubyID | undefined }) {
  return always_default_data(
    [],
    useQuery<VaultHistoryType[]>({
      enabled: Boolean(id),
      queryKey: keys.vaults.details({ id: disableKeyWhenMissing(id) })._ctx
        .history.queryKey,
      queryFn: async () => {
        const results = await graphql_price_call({
          query: query_get_daily_price,
          variables: {
            first_day_s: dayjs().subtract(1, 'year').unix().toString(),
          },
        })
        if (results == null) return []
        if (
          dayjs.unix(Number(results[0].date_s)).isSame(dayjs(), 'day') == false
        ) {
          results.unshift({ ...results[0], date_s: dayjs().unix().toString() })
        }
        results.reverse() // sort by date in ascending order
        return results
      },
      staleTime: 1000 * 60 * 60 * 24, // 1 day
      gcTime: Infinity,
    }),
  )
}
